/**
 * URLキャプチャのジョブ追跡 (docs/features/swipe-motion-clip-plan.md §11.2.4 / §11.2.8)。
 * features/swipeClip/standaloneJob.ts と同じ作り: 投入 → 生成キュー (type 'image') で追跡 → 完了で個人フォルダの一覧を更新してトースト。
 * 端末別の成否 (result_data.devices) を {@link summarizeUrlCaptureResult} でまとめ、スクロール動画モーダルの初期選択
 * (成功したスマホ版 → 無ければ成功したPC版) にも同じ結果を使う。
 */
import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';

import type { ActiveJob } from '../../components/generation/state/types';
import { AsyncJobError, waitForJobCompletion, type AsyncJob } from '../../services/asyncJobApi';
import { TEMP_QUERY_KEY } from '../materials/hooks';
import {
  URL_CAPTURE_DEVICES,
  URL_CAPTURE_DEVICE_LABELS,
  URL_CAPTURE_TRUNCATED_NOTE,
  captureHostOf,
  createUrlCaptureJob,
  urlCaptureDeviceErrorMessage,
  type UrlCaptureDevice,
  type UrlCaptureJobBody,
  type UrlCaptureJobResult,
} from './api';

interface ToastLike {
  show: (m: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
}

export interface UrlCaptureJobDeps {
  setActiveJobs: Dispatch<SetStateAction<ActiveJob[]>>;
  setShowJobQueueModal: (v: boolean) => void;
  toast: ToastLike;
  queryClient: QueryClient;
}

export interface UrlCaptureSaved {
  device: UrlCaptureDevice;
  materialId: string;
  folder: 'temporary' | 'materials';
  truncated: boolean;
}

export interface UrlCaptureFailed {
  device: UrlCaptureDevice;
  message: string;
}

export interface UrlCaptureSummary {
  host: string;
  saved: UrlCaptureSaved[];
  failed: UrlCaptureFailed[];
  /** スクロール動画モーダルで開く素材: 成功したスマホ版 → 無ければ成功したPC版 (11.2.8)。全端末失敗なら null */
  primary: UrlCaptureSaved | null;
  /** 片方だけ成功したときの注記「PC版の取得に失敗しました (スマホ版は保存済み)」。それ以外は null */
  partialNote: string | null;
}

function joinLabels(devices: UrlCaptureDevice[]): string {
  return devices.map((d) => `${URL_CAPTURE_DEVICE_LABELS[d]}版`).join('・');
}

/** 要求した端末の順 (スマホ → PC) で成否をまとめる。完了時に pending のまま / 結果が無い端末は失敗として扱う。 */
export function summarizeUrlCaptureResult(
  result: Partial<UrlCaptureJobResult> | null | undefined,
  requested: readonly UrlCaptureDevice[],
  fallbackHost: string,
): UrlCaptureSummary {
  const order = URL_CAPTURE_DEVICES.filter((d) => requested.includes(d));
  const saved: UrlCaptureSaved[] = [];
  const failed: UrlCaptureFailed[] = [];
  for (const device of order) {
    const r = result?.devices?.[device];
    if (r?.status === 'saved' && r.material_id) {
      saved.push({ device, materialId: r.material_id, folder: r.folder === 'materials' ? 'materials' : 'temporary', truncated: r.truncated === true });
    } else {
      failed.push({ device, message: urlCaptureDeviceErrorMessage(r) });
    }
  }
  const primary = saved.find((s) => s.device === 'phone') ?? saved.find((s) => s.device === 'pc') ?? null;
  const partialNote = saved.length > 0 && failed.length > 0
    ? `${joinLabels(failed.map((f) => f.device))}の取得に失敗しました (${joinLabels(saved.map((s) => s.device))}は保存済み)`
    : null;
  return { host: result?.host || fallbackHost, saved, failed, primary, partialNote };
}

/** スクロール動画モーダルの上部の注記 (片方の端末だけ失敗): 「PC版の取得に失敗しました (理由)」。それ以外は null。 */
export function urlCapturePartialNotice(summary: UrlCaptureSummary): string | null {
  if (!summary.primary || summary.failed.length === 0) return null;
  return summary.failed.map((f) => `${URL_CAPTURE_DEVICE_LABELS[f.device]}版の取得に失敗しました (${f.message})`).join(' / ');
}

/** 全端末失敗 (ジョブ failed) の文言: 端末の失敗理由 (一つ目) → ジョブの error_message → 既定。 */
export function urlCaptureFailureMessage(error: unknown, requested: readonly UrlCaptureDevice[], host: string): string {
  if (error instanceof AsyncJobError && error.result_data && typeof error.result_data === 'object' && 'devices' in error.result_data) {
    const summary = summarizeUrlCaptureResult(error.result_data as Partial<UrlCaptureJobResult>, requested, host);
    const first = summary.failed[0];
    if (first) return first.message;
  }
  if (error instanceof Error && error.message && error.message !== 'Job failed') return error.message;
  return `${host}のキャプチャに失敗しました`;
}

function stageText(device: UrlCaptureDevice): string {
  return `${URL_CAPTURE_DEVICE_LABELS[device]}版をキャプチャ中`;
}

/** 実行中の端末 (要求順で最初の未完了) をキューの段階表示にする。 */
function stageFor(job: AsyncJob, requested: readonly UrlCaptureDevice[]): string | undefined {
  const devices = (job.result_data as Partial<UrlCaptureJobResult> | undefined)?.devices;
  const order = URL_CAPTURE_DEVICES.filter((d) => requested.includes(d));
  const current = order.find((d) => {
    const status = devices?.[d]?.status;
    return status !== 'saved' && status !== 'failed';
  }) ?? order[order.length - 1];
  return current ? stageText(current) : undefined;
}

export interface StartedUrlCaptureJob {
  jobId: string;
  host: string;
  /** 完了で解決 (1端末以上保存)。全端末失敗・追跡失敗は利用者向け文言の Error で reject。トースト・キューの更新は済んでいる */
  done: Promise<UrlCaptureSummary>;
}

/**
 * ジョブを投入し、キューに載せて追跡を始める。投入のエラー (400 / 429 / 402) はフォームに表示させるため投げる。
 * 完了・失敗のトーストはここで出す (フォームやモーダルを閉じていても届く)。
 */
export async function startUrlCaptureJob(deps: UrlCaptureJobDeps, body: UrlCaptureJobBody): Promise<StartedUrlCaptureJob> {
  const { job_id } = await createUrlCaptureJob(body);
  const host = captureHostOf(body.url);
  const firstDevice = URL_CAPTURE_DEVICES.find((d) => body.devices.includes(d));
  deps.setActiveJobs((prev) => [...prev, {
    jobId: job_id,
    status: 'polling' as const,
    waiting: false,
    startedAt: Date.now(),
    type: 'image' as const,
    label: `${host}をキャプチャ中`,
    ...(firstDevice ? { stage: stageText(firstDevice) } : {}),
  }]);
  deps.setShowJobQueueModal(true);
  const done = trackUrlCaptureJob(deps, job_id, body.devices, host);
  // 呼び出し側が done を待たなくても未処理の reject にしない (トーストは track 側で出している)
  done.catch(() => undefined);
  return { jobId: job_id, host, done };
}

/** 完了まで追跡し、キュー・個人フォルダの一覧・トーストに反映する。 */
export async function trackUrlCaptureJob(
  deps: UrlCaptureJobDeps,
  jobId: string,
  requested: readonly UrlCaptureDevice[],
  host: string,
): Promise<UrlCaptureSummary> {
  const { setActiveJobs, toast, queryClient } = deps;
  let job: AsyncJob & { result_data: UrlCaptureJobResult };
  try {
    job = await waitForJobCompletion<UrlCaptureJobResult>(jobId, {
      skipVisibilityCheck: true,
      onProgress: (info) => {
        const stage = stageFor(info, requested);
        setActiveJobs((prev) => prev.map((j) => (
          j.jobId === jobId && j.status === 'polling' ? { ...j, waiting: info.status === 'pending', ...(stage ? { stage } : {}) } : j
        )));
      },
    });
  } catch (error) {
    console.error('URL capture job failed:', error);
    const message = urlCaptureFailureMessage(error, requested, host);
    setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, waiting: false, label: `${host}のキャプチャ`, error: message } : j)));
    toast.error(message);
    throw new Error(message);
  }

  const summary = summarizeUrlCaptureResult(job.result_data, requested, host);
  void queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
  if (!summary.primary) {
    // completed なのに保存済みの端末が無い (契約上は failed になるはず)。失敗として扱う
    const message = summary.failed[0]?.message ?? `${summary.host}のキャプチャに失敗しました`;
    setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, waiting: false, label: `${summary.host}のキャプチャ`, error: message } : j)));
    toast.error(message);
    throw new Error(message);
  }
  const materialId = summary.primary.materialId;
  setActiveJobs((prev) => prev.map((j) => (
    j.jobId === jobId
      ? { ...j, status: 'completed' as const, waiting: false, label: `${summary.host}のキャプチャ`, result: { id: materialId, material_id: materialId } }
      : j
  )));
  toast.success(`${summary.host}のキャプチャを個人フォルダに保存しました`);
  if (summary.partialNote) toast.show(summary.partialNote);
  if (summary.saved.some((s) => s.truncated)) toast.show(URL_CAPTURE_TRUNCATED_NOTE);
  return summary;
}
